import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  AspectRatio,
  VideoSource,
  AiArtGeneratorResult,
} from "@/constants/tools/ai-art-generator";
import { ArtType } from "@/constants/tools/ai-art-generator/art-type";
import { ImageStyle } from "@/constants/tools/ai-art-generator/image-style";
import { Model } from "@/constants/tools/ai-art-generator/model";
import { VideoCameraControl } from "@/constants/tools/ai-art-generator/video-camera-control";
import { RestApiResponse } from "@/types/common.api";

type AiArtGeneratorGetJobsResponse = {
  list: AiArtGeneratorResult[];
  page: {
    total: number;
    totalPage: number;
    pageSize: number;
  };
};

type AiArtGeneratorCreateJobParams = {
  artType: ArtType;
  model: Model;
  videoSource?: VideoSource;
  prompt?: string;
  negativePrompt?: string;
  referenceImageFile?: File;
  referenceStrength?: number;
  aspectRatio?: AspectRatio;
  imageCount?: number;
  imageStyle?: ImageStyle;
  videoCameraControl?: VideoCameraControl;
  startFrameImageFile?: File;
  resolution?: AspectRatio;
  duration?: number;
};

type AiArtGeneratorCreateJobResponse = {
  _id: string;
  adjustedImageCount?: number;
};

type AiArtGeneratorDeleteJobParams = {
  jobId: string;
  mediaId?: string;
};

export const aiArtGeneratorApi = createApi({
  reducerPath: "tools/aiArtGenerator",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/tools/ai-art-generator",
  }),
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: true,
  tagTypes: ["AiArtGenerator"],
  endpoints: (build) => ({
    getJobs: build.query<
      AiArtGeneratorGetJobsResponse,
      | {
          page?: number;
          pageSize?: number;
        }
      | undefined
    >({
      query: ({ page, pageSize } = {}) => ({
        url: "/jobs",
        params: {
          page,
          pageSize,
        },
      }),
      transformResponse: (
        res: RestApiResponse<AiArtGeneratorGetJobsResponse>,
      ) => {
        if (!res.success || !res.data) {
          throw new Error("Failed to fetch AI art generator jobs");
        }

        return res.data;
      },
      providesTags: (result) => {
        if (!result) {
          return [];
        }

        return [
          ...result.list.map(({ _id: id }) => ({
            type: "AiArtGenerator" as const,
            id,
          })),
          { type: "AiArtGenerator", id: "LIST" },
        ];
      },
    }),

    createJob: build.mutation<
      AiArtGeneratorCreateJobResponse,
      AiArtGeneratorCreateJobParams
    >({
      query: (params) => {
        const formData = new FormData();

        const jsonData = {
          artType: params.artType,
          model: params.model,
          videoSource: params.videoSource,
          prompt: params.prompt,
          negativePrompt: params.negativePrompt,
          referenceStrength: params.referenceStrength,
          aspectRatio: params.aspectRatio,
          imageCount: params.imageCount,
          imageStyle: params.imageStyle,
          videoCameraControl: params.videoCameraControl,
          resolution: params.resolution,
          duration: params.duration,
        };

        formData.append("data", JSON.stringify(jsonData));

        if (params.referenceImageFile) {
          formData.append("referenceImageFile", params.referenceImageFile);
        }

        if (params.startFrameImageFile) {
          formData.append("startFrameImageFile", params.startFrameImageFile);
        }

        return {
          url: "/jobs",
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (
        res: RestApiResponse<AiArtGeneratorCreateJobResponse>,
      ) => {
        if (!res.success || !res.data) {
          throw new Error("Failed to create AI art generator job");
        }

        return res.data;
      },
      invalidatesTags: ["AiArtGenerator"],
    }),

    deleteJob: build.mutation<void, AiArtGeneratorDeleteJobParams>({
      query: ({ jobId, mediaId }) => ({
        url: `/jobs/${jobId}`,
        method: "DELETE",
        params: {
          mediaId,
        },
      }),
      transformResponse: (res: RestApiResponse<void>) => {
        if (!res.success) {
          throw new Error("Failed to delete AI art generator job");
        }
      },
      invalidatesTags: ["AiArtGenerator"],
    }),
  }),
});
